import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  Building2, Palette, Globe, Loader2, Check, Trash2,
  CheckCircle, Upload, Image, Link2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminTenantsTab = ({ token }) => {
  const [tenants, setTenants] = useState([]);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [newTenant, setNewTenant] = useState({
    name: '', slug: '', billing_email: '',
    branding: { company_name: '', tagline: '', primary_color: '#10B981', secondary_color: '#1E293B', accent_color: '#F59E0B', email_from_name: '' }
  });
  const [editingTenant, setEditingTenant] = useState(null);
  const [brandingEditorOpen, setBrandingEditorOpen] = useState(false);
  const [editBranding, setEditBranding] = useState({
    company_name: '', tagline: '', primary_color: '#10B981', secondary_color: '#1E293B', accent_color: '#F59E0B', email_from_name: '', support_email: ''
  });
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [domainModalOpen, setDomainModalOpen] = useState(false);
  const [domainTenant, setDomainTenant] = useState(null);
  const [newDomain, setNewDomain] = useState('');
  const [domainVerifying, setDomainVerifying] = useState(false);
  const [domainInstructions, setDomainInstructions] = useState(null);

  useEffect(() => { fetchTenants(); }, []);

  const fetchTenants = async () => {
    setTenantLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tenant/admin/tenants`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setTenants(d.tenants || []); }
    } catch (err) { console.error('Error fetching tenants:', err); }
    finally { setTenantLoading(false); }
  };

  const createTenant = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tenant/admin/tenants`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newTenant)
      });
      if (res.ok) {
        toast.success('Tenant created!');
        setShowCreateTenant(false);
        setNewTenant({ name: '', slug: '', billing_email: '', branding: { company_name: '', tagline: '', primary_color: '#10B981', secondary_color: '#1E293B', accent_color: '#F59E0B', email_from_name: '' } });
        fetchTenants();
      } else { const err = await res.json(); toast.error(err.detail || 'Failed to create tenant'); }
    } catch { toast.error('Error creating tenant'); }
  };

  const toggleTenantStatus = async (tenantId, currentStatus) => {
    const action = currentStatus === 'active' ? 'suspend' : 'activate';
    try {
      const res = await fetch(`${API_URL}/api/tenant/admin/tenants/${tenantId}/${action}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { toast.success(`Tenant ${action}d`); fetchTenants(); }
      else toast.error(`Failed to ${action} tenant`);
    } catch { toast.error('Error updating tenant'); }
  };

  const openBrandingEditor = (tenant) => {
    setEditingTenant(tenant);
    setEditBranding({
      company_name: tenant.branding?.company_name || '', tagline: tenant.branding?.tagline || '',
      primary_color: tenant.branding?.primary_color || '#10B981', secondary_color: tenant.branding?.secondary_color || '#1E293B',
      accent_color: tenant.branding?.accent_color || '#F59E0B', email_from_name: tenant.branding?.email_from_name || '',
      support_email: tenant.branding?.support_email || ''
    });
    setBrandingEditorOpen(true);
  };

  const saveBranding = async () => {
    if (!editingTenant) return;
    setBrandingSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/tenant/admin/tenants/${editingTenant.id}/branding`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editBranding)
      });
      if (res.ok) { toast.success('Branding updated!'); fetchTenants(); setBrandingEditorOpen(false); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed to update branding'); }
    } catch { toast.error('Error updating branding'); }
    finally { setBrandingSaving(false); }
  };

  const uploadLogo = async (file, logoType) => {
    if (!editingTenant || !file) return;
    const formData = new FormData();
    formData.append('file', file);
    setLogoUploading(true);
    try {
      const res = await fetch(`${API_URL}/api/tenant/admin/tenants/${editingTenant.id}/logo/${logoType}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`${logoType} logo uploaded!`);
        fetchTenants();
        setEditingTenant(prev => ({ ...prev, branding: { ...prev.branding, [`logo_${logoType}_url`]: result.path } }));
      } else { const err = await res.json(); toast.error(err.detail || 'Failed to upload logo'); }
    } catch { toast.error('Error uploading logo'); }
    finally { setLogoUploading(false); }
  };

  const openDomainModal = (tenant) => {
    setDomainTenant(tenant); setNewDomain(tenant.domain || ''); setDomainInstructions(null); setDomainModalOpen(true);
  };

  const setTenantDomain = async () => {
    if (!domainTenant || !newDomain.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/tenant/admin/tenants/${domainTenant.id}/domain`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: newDomain.trim() })
      });
      if (res.ok) { const result = await res.json(); setDomainInstructions(result.instructions); toast.success('Domain configured!'); fetchTenants(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed to set domain'); }
    } catch { toast.error('Error setting domain'); }
  };

  const verifyDomain = async () => {
    if (!domainTenant) return;
    setDomainVerifying(true);
    try {
      const res = await fetch(`${API_URL}/api/tenant/admin/tenants/${domainTenant.id}/domain/verify`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await res.json();
      if (result.verified) { toast.success('Domain verified!'); fetchTenants(); setDomainModalOpen(false); }
      else toast.error(result.message || 'Verification failed');
    } catch { toast.error('Error verifying domain'); }
    finally { setDomainVerifying(false); }
  };

  const removeDomain = async () => {
    if (!domainTenant) return;
    try {
      const res = await fetch(`${API_URL}/api/tenant/admin/tenants/${domainTenant.id}/domain`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { toast.success('Domain removed'); fetchTenants(); setDomainModalOpen(false); }
      else toast.error('Failed to remove domain');
    } catch { toast.error('Error removing domain'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-tenants-tab">
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2"><Building2 className="h-5 w-5 text-blue-400" /> White-Label Tenants (Brokers)</CardTitle>
          <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={() => setShowCreateTenant(true)}><Building2 className="h-4 w-4 mr-2" /> Add Broker</Button>
        </CardHeader>
        <CardContent>
          {tenantLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div>
          ) : tenants.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No tenants configured</p>
          ) : (
            <div className="space-y-4">
              {tenants.map(tenant => (
                <div key={tenant.id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tenant.branding?.primary_color || '#10B981' }}>
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{tenant.name}</p>
                      <p className="text-xs text-slate-400">Slug: <code className="text-emerald-400">{tenant.slug}</code>
                        {tenant.domain && <span className="ml-2">Domain: {tenant.domain}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right"><p className="text-white font-medium">{tenant.total_users || 0} users</p><p className="text-xs text-slate-400">{tenant.pro_users || 0} pro</p></div>
                    <Badge className={tenant.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : tenant.status === 'trial' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}>{tenant.status}</Badge>
                    {tenant.domain_verified && <Badge className="bg-blue-500/20 text-blue-400"><CheckCircle className="w-3 h-3 mr-1" />Domain</Badge>}
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: tenant.branding?.primary_color }} title="Primary" />
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: tenant.branding?.accent_color }} title="Accent" />
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openBrandingEditor(tenant)} data-testid={`edit-branding-${tenant.slug}`}>
                      <Palette className="h-4 w-4 mr-1" /> Branding
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openDomainModal(tenant)} data-testid={`edit-domain-${tenant.slug}`}>
                      <Globe className="h-4 w-4 mr-1" /> Domain
                    </Button>
                    <Button size="sm" variant={tenant.status === 'active' ? 'destructive' : 'default'} onClick={() => toggleTenantStatus(tenant.id, tenant.status)} disabled={tenant.slug === 'default'}>
                      {tenant.status === 'active' ? 'Suspend' : 'Activate'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {showCreateTenant && (
        <Card className="bg-slate-900 border-slate-700">
          <CardHeader><CardTitle className="text-white flex items-center gap-2"><Palette className="h-5 w-5 text-purple-400" /> Create New Broker Tenant</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm text-slate-400">Tenant Name</label>
                <Input placeholder="ABC Broker Trading Game" value={newTenant.name} onChange={(e) => setNewTenant(t => ({ ...t, name: e.target.value }))} className="bg-slate-800 border-slate-700" /></div>
              <div><label className="text-sm text-slate-400">Slug (URL identifier)</label>
                <Input placeholder="abc-broker" value={newTenant.slug} onChange={(e) => setNewTenant(t => ({ ...t, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))} className="bg-slate-800 border-slate-700" /></div>
              <div><label className="text-sm text-slate-400">Billing Email</label>
                <Input placeholder="billing@broker.com" value={newTenant.billing_email} onChange={(e) => setNewTenant(t => ({ ...t, billing_email: e.target.value }))} className="bg-slate-800 border-slate-700" /></div>
              <div><label className="text-sm text-slate-400">Company Name</label>
                <Input placeholder="ABC Broker" value={newTenant.branding.company_name} onChange={(e) => setNewTenant(t => ({ ...t, branding: { ...t.branding, company_name: e.target.value } }))} className="bg-slate-800 border-slate-700" /></div>
              <div className="col-span-2"><label className="text-sm text-slate-400">Tagline</label>
                <Input placeholder="Trade Smart, Win Big" value={newTenant.branding.tagline} onChange={(e) => setNewTenant(t => ({ ...t, branding: { ...t.branding, tagline: e.target.value } }))} className="bg-slate-800 border-slate-700" /></div>
              <div><label className="text-sm text-slate-400">Primary Color</label>
                <div className="flex gap-2">
                  <input type="color" value={newTenant.branding.primary_color} onChange={(e) => setNewTenant(t => ({ ...t, branding: { ...t.branding, primary_color: e.target.value } }))} className="w-12 h-10 rounded cursor-pointer" />
                  <Input value={newTenant.branding.primary_color} onChange={(e) => setNewTenant(t => ({ ...t, branding: { ...t.branding, primary_color: e.target.value } }))} className="bg-slate-800 border-slate-700" />
                </div>
              </div>
              <div><label className="text-sm text-slate-400">Accent Color</label>
                <div className="flex gap-2">
                  <input type="color" value={newTenant.branding.accent_color} onChange={(e) => setNewTenant(t => ({ ...t, branding: { ...t.branding, accent_color: e.target.value } }))} className="w-12 h-10 rounded cursor-pointer" />
                  <Input value={newTenant.branding.accent_color} onChange={(e) => setNewTenant(t => ({ ...t, branding: { ...t.branding, accent_color: e.target.value } }))} className="bg-slate-800 border-slate-700" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateTenant(false)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={createTenant} disabled={!newTenant.name || !newTenant.slug || !newTenant.billing_email}>
                <Building2 className="h-4 w-4 mr-2" /> Create Tenant
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Branding Editor Modal */}
      <Dialog open={brandingEditorOpen} onOpenChange={setBrandingEditorOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Palette className="h-5 w-5 text-purple-400" /> Edit Branding: {editingTenant?.name}</DialogTitle>
            <DialogDescription>Customize colors, logos, and text for this tenant's white-label experience.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="p-4 rounded-lg border border-slate-700" style={{ backgroundColor: editBranding.secondary_color }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: editBranding.primary_color }}><Building2 className="h-5 w-5 text-white" /></div>
                  <div>
                    <p className="font-bold text-white">{editBranding.company_name || 'Company Name'}</p>
                    <p className="text-sm" style={{ color: editBranding.accent_color }}>{editBranding.tagline || 'Your tagline here'}</p>
                  </div>
                </div>
                <Badge style={{ backgroundColor: `${editBranding.primary_color}30`, color: editBranding.primary_color }}>Preview</Badge>
              </div>
              <div className="flex gap-2">
                <button className="px-4 py-2 rounded text-white text-sm font-medium" style={{ backgroundColor: editBranding.primary_color }}>Primary Button</button>
                <button className="px-4 py-2 rounded text-white text-sm font-medium" style={{ backgroundColor: editBranding.accent_color }}>Accent Button</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm text-slate-400">Company Name</label>
                <Input value={editBranding.company_name} onChange={(e) => setEditBranding(b => ({ ...b, company_name: e.target.value }))} className="bg-slate-800 border-slate-700" data-testid="branding-company-name" /></div>
              <div><label className="text-sm text-slate-400">Email From Name</label>
                <Input value={editBranding.email_from_name} onChange={(e) => setEditBranding(b => ({ ...b, email_from_name: e.target.value }))} className="bg-slate-800 border-slate-700" /></div>
              <div className="col-span-2"><label className="text-sm text-slate-400">Tagline</label>
                <Input value={editBranding.tagline} onChange={(e) => setEditBranding(b => ({ ...b, tagline: e.target.value }))} className="bg-slate-800 border-slate-700" /></div>
              <div className="col-span-2"><label className="text-sm text-slate-400">Support Email</label>
                <Input value={editBranding.support_email} onChange={(e) => setEditBranding(b => ({ ...b, support_email: e.target.value }))} placeholder="support@broker.com" className="bg-slate-800 border-slate-700" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[
                { key: 'primary_color', label: 'Primary Color' },
                { key: 'secondary_color', label: 'Secondary Color' },
                { key: 'accent_color', label: 'Accent Color' }
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="text-sm text-slate-400">{label}</label>
                  <div className="flex gap-2 mt-1">
                    <input type="color" value={editBranding[key]} onChange={(e) => setEditBranding(b => ({ ...b, [key]: e.target.value }))} className="w-12 h-10 rounded cursor-pointer" data-testid={key === 'primary_color' ? 'branding-primary-color' : undefined} />
                    <Input value={editBranding[key]} onChange={(e) => setEditBranding(b => ({ ...b, [key]: e.target.value }))} className="bg-slate-800 border-slate-700 font-mono text-sm" />
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-4">
              <h4 className="text-white font-medium flex items-center gap-2"><Image className="h-4 w-4 text-purple-400" /> Logos</h4>
              <div className="grid grid-cols-3 gap-4">
                {['light', 'dark', 'favicon'].map((logoType) => (
                  <div key={logoType} className="space-y-2">
                    <label className="text-sm text-slate-400 capitalize">{logoType === 'light' ? 'Logo (Light)' : logoType === 'dark' ? 'Logo (Dark)' : 'Favicon'}</label>
                    <div className="border border-dashed border-slate-700 rounded-lg p-4 text-center bg-slate-800/50">
                      {editingTenant?.branding?.[`logo_${logoType}_url`] || editingTenant?.branding?.favicon_url ? (
                        <div className="text-emerald-400 text-xs mb-2"><CheckCircle className="h-4 w-4 inline mr-1" />Uploaded</div>
                      ) : (<Upload className="h-8 w-8 mx-auto text-slate-500 mb-2" />)}
                      <input type="file" accept="image/*" onChange={(e) => uploadLogo(e.target.files[0], logoType)} className="hidden" id={`logo-upload-${logoType}`} disabled={logoUploading} />
                      <label htmlFor={`logo-upload-${logoType}`} className="cursor-pointer text-sm text-purple-400 hover:text-purple-300">
                        {logoUploading ? 'Uploading...' : 'Click to upload'}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBrandingEditorOpen(false)}>Cancel</Button>
            <Button className="bg-purple-600 hover:bg-purple-500" onClick={saveBranding} disabled={brandingSaving} data-testid="save-branding-btn">
              {brandingSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />} Save Branding
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Domain Management Modal */}
      <Dialog open={domainModalOpen} onOpenChange={setDomainModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Globe className="h-5 w-5 text-blue-400" /> Custom Domain: {domainTenant?.name}</DialogTitle>
            <DialogDescription>Configure a custom domain for this tenant's white-label experience.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {domainTenant?.domain && (
              <div className="p-3 rounded-lg bg-slate-800 border border-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Link2 className="h-4 w-4 text-blue-400" /><span className="text-white font-mono">{domainTenant.domain}</span></div>
                  {domainTenant.domain_verified ? (<Badge className="bg-emerald-500/20 text-emerald-400"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>) : (<Badge className="bg-amber-500/20 text-amber-400">Pending</Badge>)}
                </div>
              </div>
            )}
            <div>
              <label className="text-sm text-slate-400">Domain Name</label>
              <Input value={newDomain} onChange={(e) => setNewDomain(e.target.value.toLowerCase())} placeholder="game.yourbroker.com" className="bg-slate-800 border-slate-700 font-mono" data-testid="domain-input" />
              <p className="text-xs text-slate-500 mt-1">Enter the full domain (e.g., game.broker.com)</p>
            </div>
            {domainInstructions && (
              <div className="p-4 rounded-lg bg-blue-950/30 border border-blue-900/50">
                <h4 className="text-blue-400 font-medium mb-2">DNS Configuration Required</h4>
                <ol className="space-y-2 text-sm text-slate-300">
                  <li>1. {domainInstructions.step1}</li><li>2. {domainInstructions.step2}</li>
                  <li>3. {domainInstructions.step3}</li><li>4. {domainInstructions.step4}</li>
                </ol>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={setTenantDomain} disabled={!newDomain.trim()}>
                <Globe className="h-4 w-4 mr-2" /> {domainTenant?.domain ? 'Update Domain' : 'Set Domain'}
              </Button>
              {domainTenant?.domain && !domainTenant?.domain_verified && (
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-500" onClick={verifyDomain} disabled={domainVerifying} data-testid="verify-domain-btn">
                  {domainVerifying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />} Verify Domain
                </Button>
              )}
            </div>
            {domainTenant?.domain && (
              <Button variant="destructive" size="sm" className="w-full" onClick={removeDomain}><Trash2 className="h-4 w-4 mr-2" /> Remove Domain</Button>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDomainModalOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
